'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import ArticleCard from '@/components/ArticleCard';
import NewsletterBlock from '@/components/NewsletterBlock';
import AdCard from '@/components/AdCard';
import { createClient } from '@/lib/supabase/client';
import { Clock, User, ChevronRight, Share2, Mail, Copy, CheckCheck, BookOpen, AlertCircle } from 'lucide-react';

interface DbArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  category_type: string;
  tag: string;
  excerpt: string;
  body: string;
  author: string;
  published_at: string;
  read_time: number;
  hero_image_url: string;
}

function dbToArticle(a: DbArticle) {
  return {
    id: a.id,
    slug: a.slug,
    title: a.title,
    category: a.category,
    categoryType: (a.category_type as 'hot' | 'analysis'),
    subTag: (a.tag as 'HOT' | 'EXCLUSIVE' | 'ANALYSIS' | 'REPORT' | 'BREAKING' | 'SPONSORED'),
    excerpt: a.excerpt || '',
    body: a.body || '',
    author: a.author,
    authorTitle: 'Staff Writer',
    date: new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    readTime: a.read_time || 5,
    image: a.hero_image_url || 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&q=80',
    imageAlt: `${a.title} — featured article image`,
    relatedSlugs: [] as string[],
  };
}

export default function ArticleDetailContent() {
  const params = useSearchParams();
  const slug = params?.get('slug') || '';

  const [article, setArticle] = useState<ReturnType<typeof dbToArticle> | null>(null);
  const [related, setRelated] = useState<ReturnType<typeof dbToArticle>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function fetchArticle() {
      setLoading(true);
      setError('');
      try {
        const supabase = createClient();

        // Fetch the article by slug, or fall back to most recent
        let data: DbArticle | null = null;
        if (slug) {
          const { data: bySlug } = await supabase
            .from('articles')
            .select('*')
            .eq('slug', slug)
            .single();
          data = bySlug;
        }

        // Fallback: get most recent article
        if (!data) {
          const { data: recent } = await supabase
            .from('articles')
            .select('*')
            .order('published_at', { ascending: false })
            .limit(1)
            .single();
          data = recent;
        }

        if (!data) {
          setError('Article not found.');
          setLoading(false);
          return;
        }

        const mapped = dbToArticle(data);
        setArticle(mapped);

        // Fetch related articles (same category, different slug)
        const { data: relatedData } = await supabase
          .from('articles')
          .select('id, slug, title, category, category_type, tag, excerpt, body, author, published_at, read_time, hero_image_url')
          .eq('category', data.category)
          .neq('slug', data.slug)
          .order('published_at', { ascending: false })
          .limit(3);

        setRelated((relatedData || []).map(dbToArticle));
      } catch {
        setError('Failed to load article.');
      } finally {
        setLoading(false);
      }
    }

    fetchArticle();
  }, [slug]);

  function handleCopyLink() {
    navigator.clipboard?.writeText(window.location?.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <>
        <Header />
        <main className="pb-20 min-h-screen flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </main>
        <Footer />
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Header />
        <main className="pb-20 min-h-screen">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-20 text-center">
            <AlertCircle size={48} className="text-muted-foreground mx-auto mb-4" />
            <h1 className="text-xl font-700 text-foreground mb-2">{error || 'Article not found'}</h1>
            <Link href="/" className="btn-primary inline-flex mt-4">Back to Home</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Parse article body into sections
  const bodyLines = article.body?.split('\n')?.filter(Boolean);

  return (
    <>
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <div className="relative h-[420px] lg:h-[520px] overflow-hidden">
          <AppImage
            src={article.image}
            alt={article.imageAlt}
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pb-8 w-full">
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`text-xs font-700 px-2 py-1 rounded uppercase tracking-wide ${
                      article.categoryType === 'hot' ? 'tag-hot' : 'tag-editorial'
                    }`}
                  >
                    {article.subTag}
                  </span>
                  <span className="text-white/80 text-xs font-700 uppercase tracking-wide">
                    {article.category}
                  </span>
                </div>
                <h1 className="text-hero-md font-800 text-white leading-tight">
                  {article.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-white/70 text-sm">
                  <span className="flex items-center gap-1.5">
                    <User size={14} />
                    <span className="font-600">{article.author}</span>
                  </span>
                  <span>{article.date}</span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} />
                    {article.readTime} min read
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          {/* Breadcrumb */}
          <nav className="py-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link
              href={`/search?q=${encodeURIComponent(article.category)}`}
              className="hover:text-primary transition-colors"
            >
              {article.category}
            </Link>
            <ChevronRight size={12} />
            <span className="text-foreground truncate max-w-xs">{article.title}</span>
          </nav>

          {/* Content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 pb-10">
            {/* Article body */}
            <article className="lg:col-span-2">
              {/* Excerpt / intro */}
              <p className="text-lg text-foreground font-500 leading-relaxed border-l-4 border-border pl-4 mb-6">
                {article.excerpt}
              </p>

              {/* Body */}
              <div className="prose-body">
                {bodyLines?.map((line, i) => {
                  if (line?.startsWith('## ')) {
                    return (
                      <h2 key={`body-h2-${i}`} className="text-xl font-700 text-foreground mt-8 mb-3">
                        {line?.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (line?.startsWith('### ')) {
                    return (
                      <h3 key={`body-h3-${i}`} className="text-lg font-600 text-foreground mt-6 mb-2">
                        {line?.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (line?.startsWith('> ')) {
                    return (
                      <blockquote key={`body-bq-${i}`} className="pull-quote my-6">
                        {line?.replace('> ', '')}
                      </blockquote>
                    );
                  }
                  if (line?.startsWith('**Key compliance actions')) {
                    return (
                      <div key={`body-callout-${i}`} className="data-callout my-6">
                        <p className="text-sm font-700 text-foreground mb-2">Key Compliance Actions</p>
                        <p className="text-sm text-muted-foreground">{line?.replace(/\*\*/g, '')}</p>
                      </div>
                    );
                  }
                  if (line?.startsWith('- ')) {
                    return (
                      <li key={`body-li-${i}`} className="ml-5 text-base text-secondary-foreground leading-relaxed mb-1 list-disc">
                        {line?.replace('- ', '')}
                      </li>
                    );
                  }
                  if (line?.match(/^\d+\./)) {
                    return (
                      <li key={`body-ol-${i}`} className="ml-5 text-base text-secondary-foreground leading-relaxed mb-1 list-decimal">
                        {line?.replace(/^\d+\. /, '')}
                      </li>
                    );
                  }
                  if (line?.startsWith('**') && line?.endsWith('**')) {
                    return (
                      <p key={`body-bold-${i}`} className="text-base font-700 text-foreground mt-4 mb-2">
                        {line?.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  return (
                    <p key={`body-p-${i}`} className="text-base text-secondary-foreground leading-relaxed mb-4">
                      {line}
                    </p>
                  );
                })}
              </div>

              {/* Inline data callout */}
              <div className="data-callout my-8">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={16} className="text-primary" />
                  <span className="section-eyebrow">Key Data Point</span>
                </div>
                <p className="text-2xl font-800 text-foreground">
                  {article.category === 'Supply Chain' ? '47%' :
                   article.category === 'Compounding' ? '61%' :
                   article.category === 'Regulatory & Global' ? '38' : '$2.1B+'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {article.category === 'Supply Chain' ? 'Increase in domestic API supplier inquiries since Import Alert 66-40 expansion (May 2026)'
                    : article.category === 'Compounding' ? 'Of tracked U.S. peptide suppliers now provide third-party COA documentation (up from 34% in 2024)'
                    : article.category === 'Regulatory & Global'? 'Foreign peptide API manufacturers added to Import Alert 66-40 in May 2026' :'Total estimated U.S. peptide research market size as of Q2 2026'}
                </p>
              </div>

              {/* Share */}
              <div className="border-t border-border pt-6 mt-8">
                <p className="text-sm font-600 text-muted-foreground mb-3 uppercase tracking-wide text-xs">Share this article</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:border-primary hover:text-primary transition-colors text-sm font-600"
                    aria-label="Copy link to article"
                  >
                    {copied ? <CheckCheck size={16} className="text-green-600" /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(article.excerpt)}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:border-primary hover:text-primary transition-colors text-sm font-600"
                    aria-label="Share via email"
                  >
                    <Mail size={16} />
                    Email
                  </a>
                  <a
                    href={`https://x.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent('https://usapeptidepros.com')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:border-primary hover:text-primary transition-colors text-sm font-600"
                    aria-label="Share on X (Twitter)"
                  >
                    <Share2 size={16} />
                    Post on X
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Related Articles */}
              {related.length > 0 && (
                <div>
                  <h3 className="text-base font-700 text-foreground mb-4 pb-2 border-b border-border">
                    Related Articles
                  </h3>
                  <div className="space-y-4">
                    {related.map((rel) => (
                      <ArticleCard key={rel.id} article={rel} size="compact" />
                    ))}
                  </div>
                </div>
              )}

              {/* Ad slot */}
              <AdCard
                label="ADVERTISEMENT"
                headline="Advertise to 200K+ Peptide Professionals"
                subtext="Native placements, newsletter sponsorships, and interstitial formats. Request our media kit."
                ctaText="View Ad Placements"
                ctaHref="/advertise-page"
                image="https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80"
                imageAlt="Advertisement promoting USA Peptide Pros advertising opportunities to industry professionals"
              />

              {/* Newsletter */}
              <NewsletterBlock compact />
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
    </>
  );
}